/**
 * Gate large video uploads on network type.
 * Default: Wi‑Fi (or ethernet) only. Users can allow cellular in Live Training settings.
 *
 * Note: iOS Safari / most iOS PWAs do not expose Network Information API, so
 * transport is often "unknown" and uploads are allowed (cannot enforce).
 */

import { loadClientSettings } from "./liveTrainingSettings";

export const VIDEO_UPLOAD_WIFI_REQUIRED_CODE = "WIFI_REQUIRED";
export const VIDEO_UPLOAD_OFFLINE_CODE = "UPLOAD_OFFLINE";

export class VideoUploadNetworkError extends Error {
  constructor(message, code = VIDEO_UPLOAD_WIFI_REQUIRED_CODE) {
    super(message);
    this.name = "VideoUploadNetworkError";
    this.code = code;
  }
}

export function isWifiOnlyVideoUploadEnabled() {
  try {
    return loadClientSettings().wifiOnlyVideoUpload !== false;
  } catch {
    return true;
  }
}

/**
 * @returns {"wifi"|"cellular"|"none"|"unknown"}
 */
export function getNetworkTransport() {
  if (typeof navigator === "undefined") return "unknown";
  if (navigator.onLine === false) return "none";

  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;
  if (!conn) return "unknown";

  const type = String(conn.type || "").trim().toLowerCase();
  if (type === "wifi" || type === "ethernet" || type === "wimax") return "wifi";
  if (type === "cellular") return "cellular";
  if (type === "none") return "none";
  if (type === "bluetooth" || type === "other") return "unknown";

  return "unknown";
}

export function canUploadVideoOnCurrentNetwork() {
  if (!isWifiOnlyVideoUploadEnabled()) return true;
  const transport = getNetworkTransport();
  if (transport === "cellular" || transport === "none") return false;
  // wifi / ethernet / unknown → allow (unknown = cannot detect, e.g. iOS)
  return true;
}

/**
 * Throws VideoUploadNetworkError when the current network is blocked.
 */
export function assertVideoUploadAllowed() {
  if (!isWifiOnlyVideoUploadEnabled()) return;

  const transport = getNetworkTransport();
  if (transport === "none") {
    throw new VideoUploadNetworkError(
      "No network connection. Connect to Wi‑Fi to upload videos.",
      VIDEO_UPLOAD_OFFLINE_CODE
    );
  }
  if (transport === "cellular") {
    throw new VideoUploadNetworkError(
      "Video uploads are limited to Wi‑Fi. Connect to Wi‑Fi, or allow mobile data in Live Training settings.",
      VIDEO_UPLOAD_WIFI_REQUIRED_CODE
    );
  }
}
